import { z } from 'zod'

const usdNumberFormatter = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export function formatCents(cents: number): string {
    return usdNumberFormatter.format(cents / 100)
}

const compactNumberFormatter = Intl.NumberFormat('en-US', { notation: 'compact' })

export function formatCompactNumber(value: string | number) {
    return compactNumberFormatter.format(Number(value))
}

export function calculateTotalHoldingValueCents(
    chainWalletAssets: ChainWalletAssets[] | undefined,
): number {
    if (!chainWalletAssets) {
        return 0
    }
    return chainWalletAssets.reduce((acc, chainWalletAsset) => {
        return (
            acc +
            chainWalletAsset.nativeAsset.holdingValueCents +
            chainWalletAsset.tokens.reduce((acc, token) => acc + token.holdingValueCents, 0)
        )
    }, 0)
}

export function calculateHoldingByChainValueCents(
    chainWalletAssets: ChainWalletAssets[] | undefined,
): Record<string, number> {
    if (!chainWalletAssets) {
        return {}
    }
    return chainWalletAssets.reduce(
        (acc, chainWalletAsset) => {
            const cents =
                chainWalletAsset.nativeAsset.holdingValueCents +
                chainWalletAsset.tokens.reduce((acc, token) => acc + token.holdingValueCents, 0)
            acc[chainWalletAsset.chain] = cents

            return acc
        },
        {} as Record<string, number>,
    )
}

const zTradingChainId = z.enum(['solana-mainnet', '8453'])
export type TradingChainId = z.infer<typeof zTradingChainId>

export const zTokenAsset = z.object({
    // server/app only supports these chains defined in zTradingChainId!
    chain: zTradingChainId,
    tokenAddress: z.string(),
    symbol: z.string(),
    name: z.string(),
    balance: z.string(),
    decimals: z.number(),
    imageUrl: z.string(),
    priceCents: z.number(), // USD price from CoinGecko
    priceChange24h: z.number(), // 24h price change percentage from CoinGecko
    holdingValueCents: z.number(),
})

export type TokenAsset = z.infer<typeof zTokenAsset>

export const zNativeAsset = z.object({
    balance: z.string(),
    priceCents: z.number(),
    priceChange24h: z.number(),
    imageUrl: z.string().optional(),
    holdingValueCents: z.number(),
})

export type NativeAsset = z.infer<typeof zNativeAsset>

export const zChainWalletAssets = z.object({
    chain: zTradingChainId,
    walletAddress: z.string(),
    nativeAsset: zNativeAsset,
    tokens: z.array(zTokenAsset),
})

export type ChainWalletAssets = z.infer<typeof zChainWalletAssets>

/**
 * merges native + token assests + enforces supported trading chains
 */
export const zMergedTradingAsset = zNativeAsset.merge(zTokenAsset).merge(
    z.object({
        chain: zTradingChainId,
    }),
)

export type MergedTradingAsset = z.infer<typeof zMergedTradingAsset>
